import * as React from 'react';
import { CmdAlert, CmdLoader } from '@commander-services/gui-components';
import { useLocation } from 'wouter';
import { useQueries } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import Form from './Form';
import ActivateAccountService from './ActivateAccountService';
import Config from '../../../config';
import { LanguageContextProps, defaultLanguage } from '../LanguagesContext';
import ForgotPasswordService from '../ForgotPassword/ForgotPasswordService';
import ForgotPasswordForm from '../ForgotPassword/ForgotPasswordLayout';

interface IActivateAccountProps {
  context: LanguageContextProps;
}

const IS_STATUS_ERROR = 'error';
const IS_STATUS_OK = 'ok';
const IS_STATUS_WARNING = 'warning';

export default function ActivateAccount(props: IActivateAccountProps): JSX.Element {
  const [, navigate] = useLocation();
  const { formatMessage: f } = useIntl();
  const [isExpired, setIsExpired] = React.useState<boolean>(false);
  const [showExpirationToast, setShowExpirationToast] = React.useState<boolean>(false);

  const token = window.location.pathname.split('/').pop();

  const queries = useQueries({
    queries: [
      {
        queryKey: ['activate-account'],
        queryFn: () => ActivateAccountService.getForm(token, defaultLanguage),
        enabled: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['forgot-password'],
        queryFn: () => ForgotPasswordService.getForm(defaultLanguage),
        enabled: isExpired,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });
  const { isLoading, data } = queries[0];
  const { isLoading: isEmailFormLoading, data: emailFormData } = queries[1];

  if (data && data.status === IS_STATUS_ERROR) {
    navigate(Config.Login.forgotPasswordUrl);
  }

  React.useEffect(() => {
    if (token && data) {
      if (data.language && defaultLanguage !== data.language && props.context.updateValue) {
        props.context.updateValue('language', data.language);
      }
    }
    if (token && data && data.status === IS_STATUS_WARNING) {
      setIsExpired(true);
    }
    if (token && data && data.email) {
      setIsExpired(true);
    }
    if (!isEmailFormLoading && isExpired && data) {
      setShowExpirationToast(true);
    }
  }, [data, isEmailFormLoading, isExpired, navigate, props.context, token]);

  const closeExpirationToast = () => {
    setShowExpirationToast(false);
  };

  return (
    <div className="w-login">
      {showExpirationToast && (
        <CmdAlert
          id="token-expired"
          data={{
            title: f({ id: 'toastr.warning' }),
            message: f({ id: 'toast.auth.resetPassword.expiredToken2' }),
            type: 'warning', // (data.toast.type as string).toLowerCase(),
          }}
          isAbsolute={true}
          // onCloseCallback={closeDeactivateWarning}
        />
      )}
      {(isLoading || isEmailFormLoading) && <CmdLoader inContent={true} />}
      {!isLoading &&
        data.status === IS_STATUS_OK &&
        data &&
        data.form &&
        token &&
        data.form.inputs &&
        data.form.inputs.length > 0 && <Form data={data.form.inputs} token={token} />}
      {!isEmailFormLoading &&
        emailFormData &&
        data &&
        isExpired &&
        emailFormData.status === IS_STATUS_OK && (
          <ForgotPasswordForm
            data={emailFormData.form.inputs}
            email={data.email || ''}
            resetPasswordToken={token}
            resetPasswordCallback={closeExpirationToast}
          />
        )}
    </div>
  );
}
